import { api } from "./api";

/** Suggestion légère (Places Autocomplete). */
export interface AddressAutocompleteSuggestion {
  placeId: string;
  label: string;
}

/** Adresse structurée complète (Place Details). */
export interface AddressSuggestion {
  label: string;
  address: string;
  postalCode: string | null;
  city: string | null;
  countryCode: string | null;
  latitude: number;
  longitude: number;
}

export interface AutocompleteResponse {
  success: boolean;
  data: AddressAutocompleteSuggestion[];
  count: number;
}

export interface PlaceDetailsResponse {
  success: boolean;
  data: AddressSuggestion;
}

/**
 * Recherche de suggestions d'adresses (Google Places Autocomplete / Guyane).
 * Requiert au moins 3 caractères. Fournir un `sessionToken` stable par session de saisie.
 */
export async function searchAddressSuggestions(
  query: string,
  limit = 5,
  sessionToken?: string
): Promise<AddressAutocompleteSuggestion[]> {
  const q = (query || "").trim();
  if (q.length < 3) return [];
  const { data } = await api.get<AutocompleteResponse>("/addresses/autocomplete", {
    params: {
      q,
      limit: Math.min(limit, 10),
      ...(sessionToken ? { sessionToken } : {}),
    },
  });
  return data?.data ?? [];
}

/**
 * Détails d'un lieu après sélection (Google Place Details).
 * Termine la session Places côté facturation si `sessionToken` est fourni.
 */
/**
 * Géocodage inverse : coordonnées GPS → adresse (Guyane).
 */
export async function reverseGeocodeFromPosition(
  lat: number,
  lon: number
): Promise<AddressSuggestion | null> {
  try {
    const { data } = await api.get<PlaceDetailsResponse>("/addresses/reverse-geocode", {
      params: { lat, lon },
    });
    return data?.data ?? null;
  } catch {
    return null;
  }
}

export async function fetchPlaceDetails(
  placeId: string,
  sessionToken?: string
): Promise<AddressSuggestion | null> {
  if (!placeId?.trim()) return null;
  try {
    const { data } = await api.get<PlaceDetailsResponse>("/addresses/place-details", {
      params: {
        placeId,
        ...(sessionToken ? { sessionToken } : {}),
      },
    });
    return data?.data ?? null;
  } catch {
    return null;
  }
}
